import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, Crown } from "lucide-react";
import { recommendedTimeline } from "../constants";

export const ApplicationTimeline = ({ isPremiumUser }) => {
  const [selectedTerm, setSelectedTerm] = useState("Fall");

  const timeline = useMemo(
    () => recommendedTimeline[selectedTerm] || [],
    [selectedTerm]
  );

  const targetIntakeLabel = useMemo(() => {
    if (!timeline.length) return selectedTerm;

    const yearSet = new Set();

    timeline.forEach((item) => {
      const match = item.date.match(/(\d{4})\s*$/);
      if (match) {
        const year = Number(match[1]);
        if (!isNaN(year)) yearSet.add(year);
      }
    });

    const years = Array.from(yearSet).sort((a, b) => a - b);

    if (years.length === 0) return selectedTerm;
    if (years.length === 1) return `${selectedTerm} ${years[0]}`;

    return `${selectedTerm} ${years[0]}–${years[years.length - 1]}`;
  }, [timeline, selectedTerm]);

  const getStatusIcon = (status) => {
    return <Clock className="w-5 h-5 text-[#80e280]" />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "border-green-200 bg-green-50";
      case "current":
        return "border-[#80e280] bg-primary-50";
      case "upcoming":
        return "border-gray-200 bg-gray-50";
      default:
        return "border-gray-200 bg-gray-50";
    }
  };

  return (
    <div className="shadow-lg bg-white p-4 rounded-lg h-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-0">
            🧭 Recommended Timeline
          </h3>
          <p className="text-base text-gray-600 text-left mt-1">
            Target Intake: {targetIntakeLabel}
          </p>
        </div>

        {/* Dropdown for term selection */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600">Term</span>
          <select
            className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white text-gray-700"
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
          >
            <option value="Fall">Fall</option>
            <option value="Spring">Spring</option>
            <option value="Summer">Summer</option>
          </select>
        </div>
      </div>

      <div className="space-y-4 relative">
        {timeline.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
            className={`relative p-4 rounded-xl border-2 ${getStatusColor(
              item.status
            )} ${!isPremiumUser ? "blur-sm" : ""}`}
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 mt-1">
                {getStatusIcon(item.status)}
              </div>

              <div className="flex-1 text-left">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-900 text-base">
                    {item.title}
                  </h4>
                  <div className="text-base text-gray-600">{item.date}</div>
                </div>

                <p className="text-gray-600 mb-2 text-base">
                  {item.description}
                </p>

                {item.daysLeft !== undefined && (
                  <div className="flex items-center space-x-2 text-base">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-500">
                      {item.daysLeft > 0
                        ? `${item.daysLeft} days left`
                        : "Overdue"}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        ))}

        {/* Premium Lock Overlay */}
        {!isPremiumUser && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/90 rounded-lg">
            <div className="text-center">
              <Crown className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
              <p className="text-lg font-bold text-gray-800 mb-2">
                Premium Feature
              </p>
              <p className="text-base text-gray-600 mb-4">
                Get detailed timeline for your journey
              </p>
              <div className="flex items-center justify-center space-x-2">
                <Crown className="w-4 h-4 text-yellow-500" />
                <span className="text-base font-semibold text-gray-700">
                  Upgrade to Achiever
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
